import { immerable } from 'immer';

import { PlanFeatureEnum, StatusEnum } from 'api/enums';

export interface Plan {
    features: {
        [key in PlanFeatureEnum]?: {
            enabled: boolean;
            config: {
                _id: string;
                limit: number;
            };
        };
    };
    _id: string;
}

/* -------------------------------------------------------------------------------------------------
 * IClientLoginImageModel
 * -----------------------------------------------------------------------------------------------*/
export interface IClientLoginImageModel {
    _id: string;
    url: string;
}

/* -------------------------------------------------------------------------------------------------
 * IClientLoginImageModel
 * -----------------------------------------------------------------------------------------------*/
export interface IClientModel {
    _id: string;
    name: string;
    status: StatusEnum;
    logoUrl: string;
    signInLogoUrl: string;
    emailHeaderLogoUrl: string;
    domain: string;
    plan: Plan;

    preferences: {
        primaryColor: string;
        loginImages: IClientLoginImageModel[];
        allowSocialSharing: boolean;
        mapZoom: number;
        mapCenter: {
            latitude: string;
            longitude: string;
        };
    };
    preset?: {
        maxImageViewableDays?: number;
        maxForwardingIntegrations?: number;
    };
}

/* -------------------------------------------------------------------------------------------------
 * ClientLoginImageModel
 * -----------------------------------------------------------------------------------------------*/
export class ClientLoginImageModel {
    [immerable] = true;

    _id: string;
    url: string;

    constructor(data: Partial<IClientLoginImageModel>) {
        this._id = data._id || '';
        this.url = data.url || '';
    }

    static fromObject(data: Partial<IClientLoginImageModel>): ClientLoginImageModel {
        const model = new ClientLoginImageModel(data);

        return model;
    }

    static cloneFrom(data: Partial<IClientLoginImageModel>): ClientLoginImageModel {
        const model = new ClientLoginImageModel(data);

        return model;
    }

    toObject(): IClientLoginImageModel {
        const data: IClientLoginImageModel = {
            _id: this._id,
            url: this.url || null,
        };

        return data;
    }
}

/* -------------------------------------------------------------------------------------------------
 * ClientModel
 * -----------------------------------------------------------------------------------------------*/

export class ClientModel {
    [immerable] = true;

    _id: string;
    name: string;
    status: StatusEnum;
    logoUrl: string;
    emailHeaderLogoUrl: string;
    signInLogoUrl: string;
    domain: string;
    plan: Plan;
    preferences: {
        primaryColor: string;
        loginImages: ClientLoginImageModel[];
        allowSocialSharing: boolean;
        mapZoom: number;
        mapCenter: {
            latitude: string;
            longitude: string;
        };
    };
    preset?: {
        maxImageViewableDays?: number;
        maxForwardingIntegrations?: number;
    };

    constructor(data?: Partial<IClientModel>) {
        this._id = data._id ?? '';
        this.name = data.name ?? '';
        this.status = data.status ?? null;
        this.logoUrl = data.logoUrl ?? '';
        this.signInLogoUrl = data.signInLogoUrl ?? '';
        this.emailHeaderLogoUrl = data.emailHeaderLogoUrl ?? '';
        this.domain = data.domain ?? '';
        this.plan = data.plan;
        this.preferences = {
            primaryColor: data.preferences?.primaryColor ?? '',
            loginImages: data.preferences?.loginImages?.map((row) => ClientLoginImageModel.fromObject(row)) ?? [],
            mapZoom: data.preferences?.mapZoom ?? null,
            allowSocialSharing: data.preferences?.allowSocialSharing ?? null,
            mapCenter: {
                latitude: data.preferences?.mapCenter?.latitude ?? '',
                longitude: data.preferences?.mapCenter?.longitude ?? '',
            },
        };

        this.preset = {
            maxForwardingIntegrations: data.preset?.maxForwardingIntegrations,
            maxImageViewableDays: data.preset?.maxImageViewableDays,
        };
    }

    static fromObject(data: Partial<IClientModel>): ClientModel {
        const model = new ClientModel(data);

        return model;
    }

    static fromObjectList(data: Partial<IClientModel>[]): ClientModel[] {
        return data.map((row) => ClientModel.fromObject(row));
    }

    static cloneFrom(data: Partial<IClientModel>): ClientModel {
        const model = new ClientModel(data);

        return model;
    }

    toObject(): IClientModel {
        const data: IClientModel = {
            _id: this._id,
            name: this.name,
            status: this.status,
            logoUrl: this.logoUrl || null,
            signInLogoUrl: this.signInLogoUrl || null,
            emailHeaderLogoUrl: this.emailHeaderLogoUrl || null,
            domain: this.domain || null,
            plan: this.plan,
            preferences: {
                primaryColor: this.preferences?.primaryColor || null,
                loginImages: this.preferences ? this.preferences.loginImages.map((row) => row.toObject()) : [],
                mapZoom: this.preferences?.mapZoom,
                allowSocialSharing: this.preferences?.allowSocialSharing ?? null,
                mapCenter: {
                    latitude: this.preferences?.mapCenter?.latitude || null,
                    longitude: this.preferences?.mapCenter?.longitude || null,
                },
            },
            preset: {
                maxForwardingIntegrations: this.preset?.maxForwardingIntegrations || null,
                maxImageViewableDays: this.preset?.maxImageViewableDays || null,
            },
        };

        return data;
    }
}
